import moment from "moment";
import {PersistentCityModel} from "@/models/PersistentCityModel";
import { cityRepository } from "@/data";
import { postApi } from '@/api';

export class LastTaskRun {

  constructor(data) {
    this.data = {
      completed: null,
      remaining: null,
      duration: null,
      error: null,
      ...data
    };
  }

  get completed() {
    return this.data.completed;
  }

  get duration() {
    return this.data.duration;
  }

  get error() {
    return this.data.error;
  }
}

export class CurrentTaskRun {

  constructor(data) {
    this.data = {
      status: null,
      count: null,
      total: null,
      progress: null,
      description: null,
      summary: null,
      ...data
    };
  }

  get count() {
    return this.data.count;
  }

  get total() {
    return this.data.total;
  }

  get progress() {
    return this.data.progress;
  }

  get status() {
    return this.data.status;
  }

  get summary() {
    return this.data.summary;
  }

  get description() {
    let d = this.data.description;
    if (d) {
      return d;
    } else if (this.status === 'running') {
      return 'Running';
    } else if (this.status === 'completed') {
      return 'Completed';
    } else if (this.status === 'failed') {
      return 'Failed';
    } else {
      return 'Ready';
    }
  }

  get progressPercent() {
    if (this.progress >= 0) {
      return Math.round(100 * this.progress);
    } else if (this.total > 0) {
      return Math.round(this.count / this.total);
    } else {
      return 0;
    }
  }
}

export class Task extends PersistentCityModel {

  static collectionName = 'tasks';

  constructor(city, doc) {
    super(city, doc);
  }

  get completed() {
    return this.data.completed || 0;
  }

  get remaining() {
    return this.data.remaining || 0;
  }

  get total() {
    return this.completed + this.remaining;
  }

  get progressPercent() {
    return this.total > 0 ? Math.round(100 * this.completed / this.total) : 0;
  }

  get isRepeated() {
    return this.data.remaining != null;
  }

  get runCount() {
    return this.data.runCount || 0;
  }

  get duration() {
    return this.data.totalDuration || 0;
  }

  get durationPerRun() {
    return this.runCount > 0 ? this.duration / this.runCount : 0;
  }

  request() {
    return this.control('request');
  }

  cancel() {
    return this.control('cancel');
  }

  /** Send task commands to the administrator API; progress documents are read-only. */
  async control(action) {
    const response = await postApi(
      `/v1/admin/cities/${encodeURIComponent(this.city.id)}/tasks/${encodeURIComponent(this.id)}/${action}`
    );

    this._data = {
      ...this._data,
      ...response.task.data
    };
    this.updateCount++;

    return this;
  }

  get canRequest() {
    return !this.isStarted && !this.isRequested;
  }

  get canCancel() {
    return this.isRequested || this.isStarted;
  }

  get status() {
    return this.data.status;
  }

  get hasNotStarted() {
    return !this.status;
  }

  get isRequested() {
    return this.status === 'requested';
  }

  get isCanceled() {
    return this.status === 'canceled';
  }

  get isStarted() {
    return this.status === 'started';
  }

  get isCompleted() {
    return this.status === 'completed';
  }

  get isFailed() {
    return this.status === 'failed';
  }

  get error() {
    return this.data.error || '';
  }

  get dateCompleted() {
    return this.data.dateCompleted;
  }

  get lastRunFormatted() {
    let date = this.data.dateCompleted;
    if (date) {
      return moment(date.toDate()).fromNow();
    } else {
      return 'never';
    }
  }

  get itemCollectionName() {
    switch (this.id) {
      case 'importProperties':
      case 'importPropertyDetails':
      case 'importLatLng':
      case 'buildPropertyIndex':
      case 'cachePropertyImages':
        return 'properties';
      case 'cacheListingImages':
    }
    return null;
  }

  get itemDoneFieldName() {
    switch (this.id) {
      case 'importPropertyDetails':
        return 'recordDetailsImported';
      case 'importLatLng':
        return 'latLngImported';
      case 'buildPropertyIndex':
        return 'indexed';
      case 'cachePropertyImages':
      case 'cacheListingImages':
        return 'imagesCached';
    }
    return null;
  }

  async fetchLiveCounts() {
    const collectionName = this.itemCollectionName;
    if (collectionName) {
      const counts = await cityRepository.fetchFieldCounts(collectionName, this.itemDoneFieldName, true, {
        cityId: this.city.id
      });
      return {
        total: counts[0],
        completed: counts[1]
      };
    } else {
      return null;
    }
  }
}
